import {
  Item,
  ITEM_TYPE_DOCUMENT,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LINK,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Copy,
  Download,
  ExternalLink,
  Link,
  Unlink,
  Pencil,
  RefreshCw,
  Trash2,
  FolderInput,
} from "lucide-react";
import OneDriveIcon from "@/assets/file-icons/onedrive.svg";
import MicrosoftWordIcon from "@/assets/3rd-party/word_logo.svg";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import useDocumentLink from "@/hooks/useDocumentLink";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { buildDocumentLinkUrl } from "@/utils/domain";
import { useSharePointContext } from "@/pages/workspace/providers/SharePointContext";
import { useFileOperations } from "@/pages/workspace/hooks/useFileOperations";
import { useAfterMenuClose } from "@/pages/workspace/hooks/useAfterMenuClose";
import { useEffect, useRef } from "react";
import useWorkspace from "@/hooks/useWorkspace";
import { UploadSource } from "@/components/upload/types/upload-item";
import { logger } from "@/lib/logger";

type FileItemDropdownMenuProps = {
  item: Item;
};

const FileItemDropdownMenu = ({ item }: FileItemDropdownMenuProps) => {
  // Get all operations from context
  const {
    workspaceId,
    documentsSynchronizing,
    setBusy,
    clearBusy,
    handleDownloads,
    onSyncToSharePointClick,
    handleSharePointFileSyncClick,
    handleSyncFolder,
    handleUnlinkFolder,
    canUserEditMetadata,
    canUserDeleteFile,
    canUserMoveItems,
    reindexDocument,
    canItemBeReIndexed,
    setOpenEditDocumentDialog,
    isItemCloudSource,
    setWordInformationDialogOpen,
    setWordInformationItem,
    setMoveModalItem,
    setRenameFolderItem,
    setRenameDocumentItem,
    setEditLinkItem,
    syncPathAfterFolderRename,
    openDeleteDialog,
  } = useFileOperations();

  const {
    openSharePointDocumentInWordOffline,
    openCloudLocationChooser,
    selectedFolder,
    setSelectedFolder,
  } = useSharePointContext();
  const { postTemplate } = useDocumentLink();
  const { updateFolderMetadata } = useWorkspace();
  const waitingFolderIdRef = useRef<string | null>(null);
  const menuClose = useAfterMenuClose();

  // Derive isDocument and isFolder from item
  const isDocument = item.type === ITEM_TYPE_DOCUMENT;
  const isFolder = item.type === ITEM_TYPE_FOLDER;
  const isLink = item.type === ITEM_TYPE_LINK;

  const handleOpenWordInformationDialog = (item: Item) => {
    setWordInformationItem(item);
    setWordInformationDialogOpen(true);
  };

  // Handle folder selection from SharePoint picker
  useEffect(() => {
    if (waitingFolderIdRef.current === item.id && selectedFolder) {
      const handleFolderSelection = async () => {
        try {
          setBusy(item.id, "sync");

          // Determine folder source based on siteId presence
          const folderSource = selectedFolder.siteId
            ? UploadSource.SHAREPOINT
            : UploadSource.ONEDRIVE;

          // A linked folder mirrors its remote name — that is also what the next
          // sync enforces, so keeping a different local name here would only last
          // until then. The API refuses a remote name a sibling already answers to,
          // which is what stops the duplicate (LAR-406).
          await updateFolderMetadata.mutateAsync({
            workspaceId,
            folderId: item.id,
            requestBody: {
              folderSource: folderSource,
              driveId: selectedFolder.driveId,
              siteId: selectedFolder.siteId,
              sharepointFileId: selectedFolder.id,
              name: selectedFolder.name,
            },
          });

          showSuccessToast("Mappe er nu knyttet til OneDrive/SharePoint");
          // The link renames the folder to the remote folder's name.
          syncPathAfterFolderRename(item.id, selectedFolder.name);
        } catch (error) {
          logger.error("Failed to link workspace folder to cloud storage", {
            operation: "linkWorkspaceFolderToCloud",
            error,
            workspaceId,
            folderId: item.id,
          });
          showErrorToast(
            "Kunne ikke knytte mappe til OneDrive/SharePoint. Tjek venligst at der ikke allerede findes en mappe med samme navn."
          );
        } finally {
          clearBusy(item.id);
          waitingFolderIdRef.current = null;
          setSelectedFolder(null);
        }
      };

      handleFolderSelection();
    }
  }, [
    selectedFolder,
    item.id,
    workspaceId,
    updateFolderMetadata,
    setBusy,
    clearBusy,
    setSelectedFolder,
    syncPathAfterFolderRename,
  ]);
  // Download menu item - with submenu if multiple revisions
  const renderDownloadMenuItem = () => {
    if (isDocument && item.revisions && item.revisions.length > 1) {
      return (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <Download className="mr-2 h-4 w-4" />
            Download
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {item.revisions
              ?.sort((a, b) => b.revisionVersion - a.revisionVersion)
              .map((revision, index) => (
                <DropdownMenuItem
                  key={`${revision.blobId}-${index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloads(e, [item], revision.revisionVersion);
                  }}
                  className={`cursor-pointer ${
                    revision.revisionVersion === item.activeRevision
                      ? "font-semibold"
                      : ""
                  }`}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download version {revision.revisionVersion}
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    return (
      <DropdownMenuItem
        onClick={(e) => handleDownloads(e, [item])}
        className="cursor-pointer"
      >
        <Download className="mr-2 h-4 w-4" />
        Download
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenuContent
      align="end"
      onCloseAutoFocus={menuClose.onCloseAutoFocus}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
    >
      {!isLink && renderDownloadMenuItem()}

      {/* Upload to OneDrive - for non-cloud source documents */}
      {isDocument && !isItemCloudSource(item) && canUserEditMetadata(item) && (
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onSyncToSharePointClick(item);
          }}
          disabled={documentsSynchronizing.length > 0}
          className="cursor-pointer"
        >
          <img src={OneDriveIcon} className="size-4 mr-2" alt="OneDrive icon" />
          Upload til OneDrive
        </DropdownMenuItem>
      )}

      {/* Sync - for cloud source documents */}
      {isDocument && isItemCloudSource(item) && canUserEditMetadata(item) && (
        <DropdownMenuItem
          onClick={async (e) => {
            e.stopPropagation();

            setBusy(item.id, "sync");

            const toastBody = await handleSharePointFileSyncClick(
              item as WorkspaceDocument
            );
            if (toastBody.variant !== "default") {
              showErrorToast(toastBody.description, toastBody.title);
            } else {
              showSuccessToast(toastBody.description, toastBody.title);
            }

            clearBusy(item.id);
          }}
          disabled={documentsSynchronizing.length > 0}
          className="cursor-pointer"
        >
          <img src={OneDriveIcon} className="size-4 mr-2" alt="OneDrive icon" />
          Synkroniser
        </DropdownMenuItem>
      )}

      {/* Edit in Word Online - for cloud source documents */}
      {isDocument && isItemCloudSource(item) && canUserEditMetadata(item) && (
        <>
          <DropdownMenuItem
            onClick={async (e) => {
              e.stopPropagation();
              if (!item.driveId || !item.sharepointFileId) {
                logger.warn(
                  "User attempted to edit document missing SharePoint metadata",
                  {
                    operation: "editInWordOffline",
                    documentId: item.id,
                    documentSource: item.documentSource,
                    workspaceId: item.workspaceId,
                    hasDriveId: Boolean(item.driveId),
                    hasSharepointFileId: Boolean(item.sharepointFileId),
                  }
                );
                showErrorToast(
                  "Dokumentet mangler SharePoint/OneDrive oplysninger"
                );
                return;
              }
              await openSharePointDocumentInWordOffline(
                item.driveId,
                item.sharepointFileId
              );
            }}
            className="cursor-pointer"
          >
            <img
              src={MicrosoftWordIcon}
              className="size-4 mr-2"
              alt="Microsoft Word icon"
            />
            Rediger i Word
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async (e) => {
              e.stopPropagation();
              handleOpenWordInformationDialog(item);
            }}
            className="cursor-pointer"
          >
            <img
              src={OneDriveIcon}
              className="size-4 mr-2"
              alt="OneDrive icon"
            />
            Rediger online
          </DropdownMenuItem>
        </>
      )}

      {/* Link to OneDrive/SharePoint - for non-cloud source folders */}
      {isFolder && !isItemCloudSource(item) && canUserEditMetadata(item) && (
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            waitingFolderIdRef.current = item.id;
            menuClose.schedule(() =>
              openCloudLocationChooser(undefined, "folder", "selection", {
                // Dismissing the chooser must not leave this row armed to
                // link itself to whatever folder is selected next.
                onCancel: () => {
                  waitingFolderIdRef.current = null;
                },
              })
            );
          }}
          disabled={updateFolderMetadata.isPending}
          className="cursor-pointer"
        >
          {updateFolderMetadata.isPending ? (
            <Spinner className="size-4" />
          ) : (
            <>
              <img
                src={OneDriveIcon}
                className="size-4 mr-2"
                alt="OneDrive icon"
              />
              Knyt til OneDrive/SharePoint
            </>
          )}
        </DropdownMenuItem>
      )}

      {/* Unlink folder - for cloud-sourced folders */}
      {isFolder && isItemCloudSource(item) && canUserEditMetadata(item) && (
        <DropdownMenuItem
          onClick={async (e) => {
            e.stopPropagation();
            setBusy(item.id, "sync");
            try {
              await handleUnlinkFolder(item.id);
            } finally {
              clearBusy(item.id);
            }
          }}
          disabled={documentsSynchronizing.length > 0}
          className="cursor-pointer"
        >
          <Unlink className="mr-2 h-4 w-4" />
          Fjern link til OneDrive/SharePoint
        </DropdownMenuItem>
      )}

      {/* Sync folder - for cloud-sourced folders */}
      {isFolder && isItemCloudSource(item) && canUserEditMetadata(item) && (
        <DropdownMenuItem
          onClick={async (e) => {
            e.stopPropagation();
            setBusy(item.id, "sync");
            try {
              await handleSyncFolder(item.id);
            } finally {
              clearBusy(item.id);
            }
          }}
          disabled={documentsSynchronizing.length > 0}
          className="cursor-pointer"
        >
          <img src={OneDriveIcon} className="size-4 mr-2" alt="OneDrive icon" />
          Synkroniser mappe
        </DropdownMenuItem>
      )}

      {/* Rename - for folders where user can edit */}
      {isFolder && canUserEditMetadata(item) && (
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setRenameFolderItem(item);
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Omdøb mappe
        </DropdownMenuItem>
      )}

      {/* Rename - for documents where user can edit. A cloud-linked file is
          renamed in OneDrive/SharePoint first, so the new name survives sync. */}
      {isDocument && canUserEditMetadata(item) && (
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setRenameDocumentItem(item);
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Omdøb fil
        </DropdownMenuItem>
      )}

      {/* Open / copy / edit - for links */}
      {isLink && (
        <>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              if (item.url) {
                window.open(item.url, "_blank", "noopener,noreferrer");
              }
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Åbn link
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await navigator.clipboard.writeText(item.url ?? "");
                showSuccessToast("Linket er kopieret til udklipsholderen");
              } catch (error) {
                logger.error("Failed to copy workspace link", {
                  operation: "copyWorkspaceLink",
                  error,
                  workspaceId,
                  linkId: item.id,
                });
                showErrorToast("Kunne ikke kopiere linket");
              }
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Kopiér link
          </DropdownMenuItem>
          {canUserEditMetadata(item) && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setEditLinkItem(item);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Rediger link
            </DropdownMenuItem>
          )}
        </>
      )}

      {/* Move to... - for documents, folders and links, when the user may write */}
      {canUserMoveItems() && (
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setMoveModalItem(item);
          }}
        >
          <FolderInput className="mr-2 h-4 w-4" />
          Flyt til...
        </DropdownMenuItem>
      )}

      {/* Edit metadata - for documents where user can edit */}
      {isDocument && canUserEditMetadata(item) && (
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={async (e) => {
            e.stopPropagation();
            setOpenEditDocumentDialog(item);
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Rediger metadata
        </DropdownMenuItem>
      )}
      {isDocument && (
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={async (e) => {
            e.stopPropagation();
            const refId = await postTemplate.mutateAsync({
              destinationDocumentId: item.blobName,
              destinationSource: "Workspace",
              destinationWorkspaceId: workspaceId,
              destinationHtmlIds: [],
              isDestinationStatic: true,
            });
            if (refId) {
              const documentLinkUrl = buildDocumentLinkUrl(refId);
              navigator.clipboard.writeText(documentLinkUrl);
              showSuccessToast(
                "Reference oprettet og kopieret til udklipsholderen"
              );
            } else {
              logger.error("Document link template response has no id", {
                operation: "createDocumentLinkTemplate",
                workspaceId,
                documentId: item.id,
              });
              showErrorToast("Kunne ikke oprette reference");
            }
          }}
          disabled={postTemplate.isPending}
        >
          {postTemplate.isPending ? (
            <Spinner className="size-4" />
          ) : (
            <>
              <Link className="mr-2 h-4 w-4" />
              Opret reference
            </>
          )}
        </DropdownMenuItem>
      )}
      {isDocument && canItemBeReIndexed(item) && (
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={async (e) => {
            e.stopPropagation();

            setBusy(item.id, "reindex");
            await reindexDocument
              .mutateAsync({
                workspaceId: workspaceId,
                documentId: item.id,
              })
              .then((result) => {
                showSuccessToast(
                  result.status === "indexing"
                    ? `Kildeklargøring for ${item.name} kører i baggrunden.`
                    : `Kildeklargøring for ${item.name} er genkørt.`
                );
              })
              .catch((error) => {
                logger.error("Failed to reindex workspace document", {
                  operation: "reindexDocument",
                  error,
                  documentId: item.id,
                  workspaceId: workspaceId,
                  documentType: item.documentType,
                });
                showErrorToast(
                  `Noget gik galt under genkørsel af kildeklargøring for ${item.name}`
                );
              })
              .finally(() => {
                clearBusy(item.id);
              });
          }}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Genkør kildeklargøring
        </DropdownMenuItem>
      )}

      {/* Delete - for items user can delete */}
      {canUserDeleteFile(item) && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive cursor-pointer"
            onSelect={(e) => {
              e.stopPropagation();
              openDeleteDialog(item);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4 text-destructive" />
            Slet
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  );
};

export default FileItemDropdownMenu;
